import { useContext, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { CheckCircle, Copy, RefreshCw } from "react-feather";
import toast from "react-hot-toast";
import { BrandContext } from "@/contexts/BrandContext/context";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { programService } from "@/services/program.service";
import { buildJoinPortalUrl } from "@/utils/programShareLink";

interface ShareLinkFieldProps {
  programId: string;
  shareToken?: string;
  /** Named in the confirmation copy so a brand knows which link they're replacing. */
  programLabel: string;
  className?: string;
}

/**
 * The program's shareable join link, as a field in Program configuration.
 *
 * There is no "open" affordance on purpose — a brand shares this link, it
 * doesn't follow it.
 *
 * "Generate new link" is the answer to "our link leaked". It swaps the token and
 * nothing else: affiliates who already joined through the old link keep their
 * enrollment, code and sneakylink. Only the URL stops working — anyone opening
 * the old one gets the brand's default program and a notice, the same as any
 * other stale link.
 */
export function ShareLinkField({
  programId,
  shareToken,
  programLabel,
  className = "",
}: ShareLinkFieldProps) {
  const queryClient = useQueryClient();
  const { brandDetails } = useContext(BrandContext);
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const username = brandDetails?.username;
  const url = username && shareToken ? buildJoinPortalUrl(username, shareToken) : "";

  const rotate = useMutation({
    mutationFn: () => programService.rotateShareToken(programId),
    onSuccess: async () => {
      setConfirmOpen(false);
      // Every surface that renders a join link reads one of these.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["program", programId] }),
        queryClient.invalidateQueries({ queryKey: ["my-programs"] }),
        queryClient.invalidateQueries({ queryKey: ["program"] }),
      ]);
      toast.success("New link generated. The old one no longer works.");
    },
    onError: (err: unknown) => {
      toast.error(
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
          "Could not generate a new link"
      );
    },
  });

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Join link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  return (
    <div className={className}>
      <label className="block text-xs font-medium text-muted-foreground mb-1">
        Share link
      </label>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          readOnly
          value={url || "Generating…"}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-muted-foreground outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={handleCopy}
            disabled={!url}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              copied
                ? "bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400"
                : "bg-brand-600 text-white hover:bg-brand-700"
            }`}
          >
            {copied ? (
              <>
                <CheckCircle className="w-4 h-4" /> Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" /> Copy
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={!url || rotate.isPending}
            title="Replace this link — the current one stops working"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RefreshCw
              className={`w-4 h-4 ${rotate.isPending ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">
              {rotate.isPending ? "Generating…" : "New link"}
            </span>
          </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => rotate.mutate()}
        title="Generate a new share link?"
        message={`The current link for "${programLabel}" will stop working immediately, and anyone who opens it will see your discoverable program instead. Affiliates who already joined through it keep their place in this program. Re-share the new link with anyone who still needs it.`}
        confirmText={rotate.isPending ? "Generating…" : "Generate new link"}
        cancelText="Keep current link"
        variant="warning"
        isLoading={rotate.isPending}
      />
    </div>
  );
}

export default ShareLinkField;
